"use client"

import { ArrowRight } from "@medusajs/icons"
import { useProductPurchaseIntent } from "../../hooks/use-product-purchase-intent"
import type { StoreProductMedia, StoreTimelineSection } from "../../types"
import SafeRichText from "@modules/common/components/safe-rich-text"
import styles from "./product-page-sections.module.css"

type TimelineProps = {
  section: StoreTimelineSection
}

const TimelineMedia = ({ media }: { media?: StoreProductMedia | null }) => {
  if (!media) return null

  if (media.type === "video") {
    return (
      <video
        className={styles.timelineImage}
        src={media.url}
        poster={media.poster_url ?? undefined}
        preload="none"
        controls
      />
    )
  }

  return (
    <img
      className={styles.timelineImage}
      src={media.url}
      alt={media.alt_text ?? ""}
      loading="lazy"
    />
  )
}

export default function Timeline({ section }: TimelineProps) {
  const purchaseIntent = useProductPurchaseIntent("product-purchase-panel")
  const { content } = section
  const steps = [...content.steps].sort(
    (left, right) => (left.position ?? 0) - (right.position ?? 0)
  )
  const actionLabel = content.action_label?.trim()

  const actionContent = actionLabel ? (
    <span>
      <span className={styles.timelineButtonLabel}>{actionLabel}</span>
      <span className={styles.timelineButtonIcon} aria-hidden="true">
        <ArrowRight />
      </span>
    </span>
  ) : null

  return (
    <section className={styles.timelineSection} aria-label={content.title}>
      <div className={styles.timelineInner}>
        {content.image_media ? (
          <div className={styles.timelineMedia}>
            <TimelineMedia media={content.image_media} />
          </div>
        ) : null}

        <div className={styles.timelineContent}>
          <header className={styles.timelineHeader}>
            <h2 className={styles.timelineTitle}>{content.title}</h2>
            {content.subtitle ? (
              <p className={styles.timelineSubtitle}>{content.subtitle}</p>
            ) : null}
            <SafeRichText document={content.body} />
          </header>

          <ol className={styles.timelineList} aria-label="Kullanım adımları">
            {steps.map((step, index) => (
              <li
                key={step.id ?? `${step.position ?? index}-${step.title}`}
                className={styles.timelineItem}
                data-testid="timeline-step"
              >
                <span className={styles.timelineDot} aria-hidden="true" />
                <div className={styles.timelineItemBody}>
                  <div className={styles.timelineItemHeader}>
                    <span className={styles.timelineTag}>{step.period_label}</span>
                    <h3 className={styles.timelineItemTitle}>{step.title}</h3>
                  </div>
                  <SafeRichText document={step.body} className={styles.timelineDescription} />
                </div>
              </li>
            ))}
          </ol>

          {actionLabel && content.action_type === "add_to_cart" ? (
            <button
              type="button"
              className={styles.timelineButton}
              disabled={purchaseIntent.disabled}
              onClick={purchaseIntent.run}
            >
              {actionContent}
            </button>
          ) : null}

          {actionLabel && content.action_type === "link" && content.action_href ? (
            <a className={styles.timelineButton} href={content.action_href}>
              {actionContent}
            </a>
          ) : null}
        </div>
      </div>
    </section>
  )
}
